import { useTitle } from '@/lib/useTitle';
import { useJsonLd } from '@/lib/useJsonLd';
import { faqPageJsonLd } from '@/lib/seo';
import LocalPageSheet from '@/components/LocalPageSheet';
import type { Faq } from '@/components/LocalPageSheet';

export const meta = {
  path: '/sell-my-business-kitchener-waterloo-cambridge-guelph',
  title: 'Sell My Business — Kitchener, Waterloo, Cambridge & Guelph',
  description:
    'Confidential business-sale representation in Kitchener, Waterloo, Cambridge and Guelph, including businesses with leased premises or real estate. Prepare the sale before exposing sensitive information.',
};

const FAQS: Faq[] = [
  {
    q: 'Can a business be marketed without publicly naming it?',
    a: 'Yes. A confidential process can start with a non-identifying summary and require a confidentiality agreement before sensitive details are released. The exact process depends on the business and the information being shared.',
  },
  {
    q: 'What information should I prepare before selling my business?',
    a: 'Common starting information includes recent financial statements or tax information prepared with your accountant, a summary of assets and equipment, staffing information at an appropriate level, the premises lease, major supplier or customer dependencies, licences and any real estate included in the sale.',
  },
  {
    q: 'What if my business operates from leased space?',
    a: 'The premises lease can be central to the transaction. Assignment rights, landlord consent, remaining term, renewal options, permitted use and security requirements can all affect whether a buyer can actually take over the location.',
  },
  {
    q: 'What if I own the building too?',
    a: 'The business and real estate can be considered together or separately depending on the seller’s goals and the likely buyer pool. The structure should be coordinated with your lawyer and accountant before commitments are made.',
  },
  {
    q: 'Do you determine the tax structure of the sale?',
    a: 'No. Share-sale versus asset-sale structure and tax consequences need advice from your accountant and lawyer. Brokerage work can help coordinate the commercial terms and keep the real estate or lease component aligned with that professional advice.',
  },
];

export default function SellBusinessKWCGuelph() {
  useTitle(meta.title, meta.description);
  useJsonLd('faq-sell-business-kwc-guelph', faqPageJsonLd(FAQS));

  return (
    <LocalPageSheet
      sheetNo="S-08"
      kicker="Business owner search — confidential exit planning"
      h1="Sell my business, without broadcasting it."
      intro="Representation for business owners in Kitchener, Waterloo, Cambridge and Guelph who want a controlled sale process — particularly when a commercial lease or owned real estate is part of the transaction."
      glyph="retail"
      audiences={[
        {
          label: 'Ready to explore a sale',
          body: 'Start with what the business does, where it operates, whether the premises are leased or owned, and what you want the transition to look like. Sensitive details can stay out of the first conversation.',
          ctaLabel: 'Discuss a confidential sale',
        },
        {
          label: 'Planning ahead',
          body: 'A future sale is easier to understand when the lease, financial records, licences, equipment and owner dependencies are organized before a buyer starts asking questions.',
          ctaLabel: 'Prepare before going to market',
        },
      ]}
      localLabel="Kitchener · Waterloo · Cambridge · Guelph"
      localBody={
        <>
          <p>
            A business sale is different from simply advertising a property. Buyers may need to understand earnings, assets, staff, licences, supplier and customer concentration, the owner’s role and the location before they can assess the opportunity.
          </p>
          <p>
            When premises are leased, the real estate piece can become a gatekeeper: a strong business deal may still fail if the lease cannot be assigned or the buyer cannot obtain acceptable new terms. When the seller owns the building, the buyer pool and transaction structure can change again.
          </p>
          <p>
            The objective is a controlled process that gives qualified prospects enough information to evaluate the opportunity without putting sensitive operating details into a public advertisement.
          </p>
        </>
      }
      honestLabel="Brokerage is only one part of the sale team"
      honestBody="Business valuation, tax structure, legal terms, financial verification and regulatory issues can require accountants, lawyers, appraisers or other specialists. Brokerage representation should coordinate with that advice rather than pretend to replace it."
      faqs={FAQS}
      interest="business-listing"
      closingLine="Plan the exit before the market learns the business is for sale."
      related={[
        { to: '/business-listings', label: 'Business listings services' },
        { to: '/buying-or-selling-a-business-guelph', label: 'Business sales — Guelph' },
        { to: '/commercial-property-valuation-waterloo-region-guelph', label: 'Own the real estate too?' },
      ]}
    />
  );
}
